import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DamageAnalyticsTableService } from '../../asset-management-table/damage-analytics-table.service';
import { AssetManagementService } from '../../asset-management-service';
import {
  debounceTime,
  distinctUntilChanged,
  Subject,
  Subscription,
} from 'rxjs';
import { dropdownValues } from '@app/shared/interfaces/common.model';
import { FileUploadService } from '@app/shared/services/file-upload.service';
import { ToasterService } from '@app/shared/services/toaster.service';
@Component({
  selector: 'app-damage-analytics-form',
  templateUrl: './damage-analytics-form.component.html',
  styleUrl: './damage-analytics-form.component.scss',
})
export class DamageAnalyticsFormComponent implements OnInit, OnDestroy {
  DamageAnalyticsForm!: FormGroup;
  assetsOptions = [
    { label: 'MacBook', value: 'MacBook' },
    { label: 'Windows', value: 'Windows' },
  ];
  showForm = false;
  emailOptions: any[] = [];
  filteredEmailOptions: any[] = [];
  assetId: number = 0;
  employeeDataMap: Map<string, any> = new Map();
  attachedFiles: File[] = [];
  uploadedFileUrl: any = '';
  selectedStatus: string = 'Pending';

  // Subject for debouncing email search input
  private emailSearchSubject = new Subject<string>();
  private emailSearchSubscription?: Subscription;

  // Centralized form keys for patching and disabling
  readonly formKeys: string[] = [
    'osType',
    'damageCost',
    'employeeId',
    'fullName',
    'employer',
    'dateOfJoining',
    'dateOfPickup',
    'reportingManager',
    'dnTLeader',
    'damageCostStatus',
    'comments',
    'email',
  ];

  // Keys to disable in edit mode
  readonly disableKeys: string[] = [
    'employeeId',
    'fullName',
    'employer',
    'dateOfJoining',
    'dateOfPickup',
    'reportingManager',
    'dnTLeader',
    'email',
    'osType',
  ];
  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DamageAnalyticsFormComponent>,
    private assetManagementService: AssetManagementService,
    private damageAnalyticsService: DamageAnalyticsTableService,
    private cdr: ChangeDetectorRef,
    private fileUploadService: FileUploadService,
    private toasterService: ToasterService,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
  ) {
    this.dialogData = this.dialogData || {};
  }

  ngOnInit() {
    this.initializeForm();
    this.setupDebouncedEmailSearch();

    if (this.dialogData?.isEdit) {
      this.patchEditData();
    } else {
      this.setupEmailSearch();
    }
  }

  ngOnDestroy() {
    // Clean up subscription to prevent memory leaks
    if (this.emailSearchSubscription) {
      this.emailSearchSubscription.unsubscribe();
    }

    // Complete the subject
    this.emailSearchSubject.complete();
  }

  private setupDebouncedEmailSearch(): void {
    // Set up debounced search for email input with 300ms delay for faster UI response
    this.emailSearchSubscription = this.emailSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchTerm: string) => {
        if (searchTerm) {
          this.searchEmployeesByEmail(searchTerm.trim());
        } else if (!searchTerm || searchTerm.trim().length === 0) {
          this.emailOptions = [];
          this.filteredEmailOptions = [];
          this.cdr.detectChanges();
        }
      });
  }

  private setupEmailSearch(): void {
    // Email search is now handled only through onEmailInput with debouncing
    // No automatic form control subscription to prevent excessive API calls
  }

  onEmailInput(event: any): void {
    const searchTerm = event.target?.value || event;

    // Only search if it's a string and has minimum length
    if (typeof searchTerm === 'string' && searchTerm.trim().length >= 2) {
      // Check if it's already a selected email to avoid unnecessary API calls
      if (this.employeeDataMap.has(searchTerm)) {
        this.processEmailSelection(searchTerm);
        return;
      }

      // Use debounced search for new terms
      this.emailSearchSubject.next(searchTerm);
    } else if (!searchTerm || searchTerm.trim().length === 0) {
      // Clear options when input is empty
      this.emailOptions = [];
      this.filteredEmailOptions = [];
      this.cdr.detectChanges();
    }
  }

  private searchEmployeesByEmail(email: string): void {
    // Use cached version to prevent multiple API calls
    this.assetManagementService.searchEmployeesByEmail(email).subscribe({
      next: (response: any) => {
        this.filteredEmailOptions = response.data || response || [];

        this.employeeDataMap.clear();

        const newOptions = this.filteredEmailOptions.map((employee: any) => {
          this.employeeDataMap.set(employee.email, {
            employeeId: employee.employeeId,
            email: employee.email,
          });

          return {
            label: employee.email,
            value: employee.email,
          } as dropdownValues;
        });

        this.emailOptions = [...newOptions];

        this.cdr.detectChanges();
      },
      error: (error) => {
        this.emailOptions = [];
        this.filteredEmailOptions = [];
        this.cdr.detectChanges();
      },
    });
  }

  onEmailSelect(event: any): void {
    if (event && event.target && event.target.textContent) {
      const selectedEmail = event.target.textContent.trim();

      this.processEmailSelection(selectedEmail);
    }
  }

  onEmailChange(selectedOption: any): void {
    let selectedEmail = selectedOption;

    if (selectedOption && selectedOption.value !== undefined) {
      selectedEmail = selectedOption.value;
    } else if (
      selectedOption &&
      selectedOption.target &&
      selectedOption.target.value
    ) {
      selectedEmail = selectedOption.target.value;
    } else if (typeof selectedOption === 'string') {
      selectedEmail = selectedOption;
    }

    // Update form control value for validation
    const emailControl = this.DamageAnalyticsForm.get('email');
    if (emailControl && selectedEmail) {
      emailControl.setValue(selectedEmail);
      emailControl.markAsTouched();
    }

    if (selectedEmail && this.employeeDataMap.has(selectedEmail)) {
      this.processEmailSelection(selectedEmail);
    }
  }
  private processEmailSelection(selectedEmail: string): void {
    const employee = this.employeeDataMap.get(selectedEmail);
    if (employee?.employeeId) {
      // Use cached version to prevent multiple API calls
      this.assetManagementService
        .getEmployeeDetailsByPsid(employee.employeeId)
        .subscribe({
          next: (data: any) => this.patchAndDisableFields(data),
          error: () => this.patchBasicFields(employee, selectedEmail),
        });
    }
  }

  private patchBasicFields(employee: any, email: string): void {
    this.DamageAnalyticsForm.patchValue({
      employeeId: employee.employeeId,
      fullName: employee.fullName || employee.name || '',
      employer: employee.employer || '',
      reportingManager: employee.reportingManager || '',
      dnTLeader: employee.dnTLeader || '',
      osType: employee.osType || employee.laptopType || '',
    });
  }

  private patchAndDisableFields(data: any): void {
    const patchData = {
      fullName: data.fullName || '',
      employer: data.employer || '',
      dateOfJoining: data.dateOfJoining || '',
      dateOfPickup: data.dateOfPickup || '',
      reportingManager: data.reportingManager || '',
      dnTLeader: data.dnTLeader || '',
      employeeId: data.employeeId || '',
      osType: data.osType || data.laptopType || '',
    };
    this.DamageAnalyticsForm.patchValue(patchData);
    let fieldsToDisable = [
      'employeeId',
      'fullName',
      'employer',
      'dateOfJoining',
      'dateOfPickup',
      'reportingManager',
      'dnTLeader',
      'osType',
    ];
    if (this.dialogData?.isEdit) {
      fieldsToDisable.push('email');
    }
    fieldsToDisable.forEach((field) => {
      const control = this.DamageAnalyticsForm.get(field);
      if (control) {
        control.disable();
      }
    });
  }

  private initializeForm(): void {
    let laptopTypeValue = '';
    if (
      this.dialogData &&
      this.dialogData.osType !== undefined &&
      this.dialogData.osType !== null
    ) {
      laptopTypeValue =
        typeof this.dialogData.osType === 'object'
          ? this.dialogData.osType?.value || '' || this.dialogData.osType
          : this.dialogData.osType || '';
    } else {
      laptopTypeValue =
        this.dialogData?.osType?.value || this.dialogData?.osType || '';
    }

    let emailId;
    if (this.dialogData.isEdit) {
      emailId = this.dialogData?.email;
    } else {
      emailId = this.dialogData?.email?.value;
    }
    this.DamageAnalyticsForm = this.fb.group({
      osType: [laptopTypeValue],
      damageCost: [this.dialogData?.damageCost ?? '', Validators.required],
      employeeId: [
        { value: this.dialogData?.employeeId ?? '', disabled: true },
      ],
      fullName: [
        { value: this.dialogData?.fullName ?? '', disabled: true },

      ],
      employer: [
        { value: this.dialogData?.employer ?? '', disabled: true },
      ],
      dateOfJoining: [
        { value: this.dialogData?.dateOfJoining ?? '', disabled: true },
      ],
      dateOfPickup: [{ value: this.dialogData?.dateOfPickup ?? '', disabled: true }],
      reportingManager: [
        { value: this.dialogData?.reportingManager ?? '', disabled: true },
      ],
      dnTLeader: [
        { value: this.dialogData?.dnTLeader ?? '', disabled: true },

      ],
      damageCostStatus: [this.dialogData?.damageCostStatus ?? ''],
      comments: [this.dialogData?.comments?.[0]?.comment ?? ''],
      email: ['', Validators.required],
    });
  }

  public onEditSubmit(): void {
    const formValue = this.DamageAnalyticsForm.getRawValue();

    const enabledControls = Object.keys(
      this.DamageAnalyticsForm.controls,
    ).filter((key) => this.DamageAnalyticsForm.get(key)?.enabled);

    // Check each enabled control for errors
    const controlErrors: any = {};
    enabledControls.forEach((key) => {
      const control = this.DamageAnalyticsForm.get(key);
      if (control?.errors) {
        controlErrors[key] = control.errors;
      }
    });

    const isFormValid = enabledControls.every((key) => {
      const control = this.DamageAnalyticsForm.get(key);
      return !control?.errors;
    });

    const isMacBookSelected =
      formValue.osType === 'MacBook' ||
      formValue.osType === 'mac' ||
      formValue.osType === 'Mac';
    const hasFile =
      this.uploadedFileUrl ||
      (this.attachedFiles && this.attachedFiles.length > 0) ||
      this.dialogData?.damageUrl;

    if (isMacBookSelected && !hasFile) {
      this.toasterService.showError(
        'File attachment is required when MacBook is selected',
        'Validation Error',
      );
      this.DamageAnalyticsForm.markAllAsTouched();
      return;
    }

    if (isFormValid) {
      const formatDate = (date: any) => {
        if (!date) return '';
        const dateObj = new Date(date);
        return dateObj.toISOString().split('T')[0];
      };
      const payload = {
        employeeId: formValue.employeeId,
        osType: formValue.osType,
        damageCost: formValue.damageCost,
        dateOfPickup: formatDate(formValue.dateOfPickup),
        damageCostStatus: formValue.damageCostStatus || 'Pending',
        comments: formValue.comments,
        damageUrl: this.uploadedFileUrl || this.dialogData?.damageUrl || '',
        commentsLabel: 'damage',
      };

      this.assetManagementService
        .updateDamageAnalyticsRecord(payload)
        .subscribe({
          next: (res) => {
            if (res) {
              this.toasterService.showSuccess(
                res?.message || 'Damage analytics record updated successfully.',
                'Success',
              );
              this.showForm = false;
              this.dialogRef.close(payload);
            }
          },
          error: (err) => {
            console.error('API call failed:', err);
            this.toasterService.showError(
              'Failed to update damage record',
              'Error',
            );
          },
        });
    } else {
      this.DamageAnalyticsForm.markAllAsTouched();
    }
  }

  public onSubmit(): void {
    let requiredEnabledFields = ['damageCost'];
    if (!this.dialogData?.isEdit) {
      requiredEnabledFields.push('email');
    }

    const hasInvalidRequiredFields = requiredEnabledFields.some((key) => {
      const control = this.DamageAnalyticsForm.get(key);
      return control?.enabled && control?.invalid;
    });

    // Check MacBook file validation
    const formValue = this.DamageAnalyticsForm.getRawValue();
    const isMacBookSelected =
      formValue.osType === 'MacBook' ||
      formValue.osType === 'mac' ||
      formValue.osType === 'Mac';
    const hasFile =
      this.uploadedFileUrl ||
      (this.attachedFiles && this.attachedFiles.length > 0);

    if (isMacBookSelected && !hasFile) {
      this.toasterService.showError(
        'File attachment is required when MacBook is selected',
        'Validation Error',
      );
      this.DamageAnalyticsForm.markAllAsTouched();
      return;
    }

    if (!hasInvalidRequiredFields) {
      const formValue = this.DamageAnalyticsForm.getRawValue();
      const userProfile: any = sessionStorage.getItem('userProfile');
      const submittedTimestamp = this.generateSubmittedDate();
      const { email } = JSON.parse(userProfile);
      const formatDate = (date: any) => {
        if (!date) return '';
        const dateObj = new Date(date);
        return dateObj.toISOString().split('T')[0];
      };
      const payload = {
        employeeId: formValue.employeeId,
        osType: formValue.osType,
        damageCost: formValue.damageCost,
        dateOfPickup: formatDate(formValue.dateOfPickup),
        damageCostStatus: formValue.damageCostStatus || 'Pending',
        comments: formValue.comments,
        damageUrl:
          this.uploadedFileUrl ||
          (this.attachedFiles.length ? this.attachedFiles[0].name : ''),
        createdDate: submittedTimestamp,
        createdBy: email,
        commentsLabel: 'damage',
      };
      this.assetManagementService
        .createDamageAnalyticsRecord(payload)
        .subscribe({
          next: (res) => {
            if (res) {
              this.toasterService.showSuccess(
                res?.message || 'Damage analytics record created successfully.',
                'Success',
              );
              this.showForm = false;
              this.dialogRef.close(payload);
            }
          },
          error: (error) => {
            console.error('Submit error:', error);
          },
        });
    } else {
      this.DamageAnalyticsForm.markAllAsTouched();
    }
  }

  public closeForm(): void {
    this.dialogRef.close();
  }

  private isFileUploadSubmission: boolean = false;

  handleFormSubmit(event?: Event): void {
    if (this.isFileUploadSubmission) {
      this.isFileUploadSubmission = false;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    if (event) {
      const target = event.target as HTMLElement;
      if (
        target &&
        (target.closest('app-file-upload') ||
          target.closest('.attachment-field') ||
          (target.tagName === 'INPUT' &&
            (target as HTMLInputElement).type === 'file'))
      ) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
    if (this.dialogData?.isEdit) {
      this.onEditSubmit();
    } else {
      this.onSubmit();
    }
  }

  handleActionForm(useCase: string) {
    switch (useCase) {
      case 'save':
        this.onSubmit();
        break;
      case 'saveEdit':
        this.onEditSubmit();
        break;
      case 'cancel':
        this.closeForm();
        break;
      default:
        break;
    }
  }

  onStatusChange(newStatus: string): void {
    this.DamageAnalyticsForm.get('damageCostStatus')?.setValue(newStatus);
  }

  onAttachmentSelected(file: File): void {
    this.isFileUploadSubmission = true;

    if (!file) {
      this.isFileUploadSubmission = false;
      return;
    }

    this.attachedFiles = [file];
    this.fileUploadService
      .uploadFile(file, 'damage analytics Upload')
      .subscribe({
        next: (resp) => {
          if (resp?.success && resp?.filePath) {
            this.uploadedFileUrl = resp.filePath;
          } else {
            this.uploadedFileUrl = '';
          }

          this.isFileUploadSubmission = false;
        },
        error: (err: any) => {
          this.uploadedFileUrl = '';

          this.isFileUploadSubmission = false;
        },
      });
  }
  onAttachmentRemoved(): void {
    this.attachedFiles = [];
    this.uploadedFileUrl = '';

    if (this.dialogData?.isEdit && this.dialogData?.damageUrl) {
      this.dialogData.damageUrl = '';
    }
    this.damageAnalyticsService.setAttachment(null);
  }

  isFormInvalid(): boolean {
    if (!this.DamageAnalyticsForm) {
      return true;
    }

    let requiredEnabledFields = ['damageCost'];
    if (!this.dialogData?.isEdit) {
      requiredEnabledFields.push('email');
    }

    const hasInvalidRequiredFields = requiredEnabledFields.some((key) => {
      const control = this.DamageAnalyticsForm.get(key);
      return control?.enabled && control?.invalid;
    });

    // Check if MacBook is selected and file is required
    const osTypeValue = this.DamageAnalyticsForm.get('osType')?.value;
    const isMacBookSelected = osTypeValue === 'MacBook';

    if (isMacBookSelected) {
      const hasFile =
        this.uploadedFileUrl ||
        (this.attachedFiles && this.attachedFiles.length > 0) ||
        (this.dialogData?.isEdit && this.dialogData?.damageUrl);
      if (!hasFile) {
        return true;
      }
    }

    return hasInvalidRequiredFields;
  }

  private patchEditData(): void {
    this.assetId = this.dialogData.assetId;

    if (this.dialogData.damageUrl) {
      this.uploadedFileUrl = '';
    } else {
      this.uploadedFileUrl = '';
    }

    const patchObj: any = {};
    this.formKeys.forEach((key) => {
      if (key === 'osType') {
        patchObj[key] =
          this.dialogData.osType?.value || this.dialogData.osType || '';
      } else if (key === 'comments') {
        // Extract the comment string from the comments array
        patchObj[key] = this.dialogData.comments?.[0]?.comment ?? '';
      } else {
        patchObj[key] = this.dialogData[key] ?? '';
      }
    });

    this.DamageAnalyticsForm.patchValue(patchObj);

    // Patch disabled fields specifically to ensure they get the values
    this.disableKeys.forEach((field) => {
      const control = this.DamageAnalyticsForm.get(field);
      if (control) {
        // Set value before disabling to ensure it gets updated
        if (this.dialogData[field] !== undefined) {
          control.setValue(this.dialogData[field]);
        }
        control.disable();
      }
    });
  }

  private generateSubmittedDate(): string {
    const now = new Date();
    const utcMillis = now.getTime() + now.getTimezoneOffset() * 60000;
    const istDate = new Date(utcMillis + 330 * 60000);

    const pad = (n: number, l = 2) => String(n).padStart(l, '0');

    const submittedDate =
      `${istDate.getFullYear()}-` +
      `${pad(istDate.getMonth() + 1)}-` +
      `${pad(istDate.getDate())}T` +
      `${pad(istDate.getHours())}:` +
      `${pad(istDate.getMinutes())}:` +
      `${pad(istDate.getSeconds())}.` +
      `${pad(istDate.getMilliseconds(), 3)}`;

    return submittedDate;
  }

  getFileNameFromUrl(url: string): string {
    if (!url) return '';
    try {
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || '';
    } catch (error) {
      return '';
    }
  }
}
